import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  MapPin,
  Save,
  Loader2,
  ChevronDown,
  AlertCircle,
  ArrowLeft,
  Shield,
  Users,
  Phone,
  Navigation,
  Globe,
  Star,
  ChevronRight,
  ChevronLeft as ChevronLeftIcon,
  Info,
  CheckCircle
} from "lucide-react";
import { regionOptions, locationsByRegion } from "../../../utils/data";
import Navbar from "../../../components/Navbar";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { PERMISSIONS } from "../../../config/permissions";
import Loader from "../../../components/Loader";
import branchService from "../../../requests/branches/BranchService";

const OptionalLabel = () => (
  <span className="ml-1 text-xs text-gray-400">(optional)</span>
);

const ValidationError = ({ message }) => (
  <motion.p
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="flex gap-1 items-center mt-1 text-sm text-red-500"
  >
    <AlertCircle className="w-4 h-4" />
    {message}
  </motion.p>
);

const StepIndicator = ({ currentStep, totalSteps, onStepClick }) => {
  const steps = [
    { id: 1, title: "Basic Info", icon: Building2 },
    { id: 2, title: "Location", icon: MapPin },
    { id: 3, title: "Details", icon: Info },
    { id: 4, title: "Review", icon: CheckCircle }
  ];

  return (
    <div className="flex justify-between items-center mb-8">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;
        const isClickable = currentStep > step.id;

        return (
          <div key={step.id} className="flex items-center">
            <motion.button
              whileHover={isClickable ? { scale: 1.05 } : {}}
              whileTap={isClickable ? { scale: 0.95 } : {}}
              onClick={() => isClickable && onStepClick(step.id)}
              disabled={!isClickable}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isActive
                  ? "text-white bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] shadow-lg"
                  : isCompleted
                  ? "text-green-700 bg-green-100 hover:bg-green-200"
                  : "text-gray-400 bg-gray-100"
              } ${isClickable ? "cursor-pointer" : "cursor-default"}`}
            >
              <div className={`p-2 rounded-lg ${
                isActive 
                  ? "bg-white/20" 
                  : isCompleted 
                  ? "bg-green-200" 
                  : "bg-gray-200"
              }`}>
                <Icon className="w-4 h-4" />
              </div>
              <span className="font-medium">{step.title}</span>
            </motion.button>
            
            {index < steps.length - 1 && (
              <div className={`w-8 h-0.5 mx-2 ${
                currentStep > step.id ? "bg-green-400" : "bg-gray-200"
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const BranchEdit = () => {
  const { branch_slug } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Permission checks
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor 
  } = usePermissionCheck();

  // Check if user can edit branches
  const canEditBranches = canPerform(PERMISSIONS.MODULES.BRANCH_MANAGEMENT, PERMISSIONS.ACTIONS.EDIT_BRANCH) || isMainVendor;

  // Debug logging for permission checks
  console.log('BranchEdit Permission Debug:', {
    isAuthenticated,
    isMainVendor,
    canEditBranches,
    branch_slug
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [branchData, setBranchData] = useState({
    name: "",
    region: "",
    location: "",
    customLocation: "",
    district: "",
    suburb: "",
    landmark: "",
    gpsAddress: "",
    googleMapsLink: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    phoneNumber: "",
    region: "",
    location: "",
    customLocation: "",
  });

  const [availableLocations, setAvailableLocations] = useState([]);
  const [isCustomLocation, setIsCustomLocation] = useState(false);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Fetch branch details
  useEffect(() => {
    const fetchBranchDetails = async () => {
      if (!canEditBranches) return; // Don't fetch if no permission
      
      try {
        setIsLoading(true);
        const response = await branchService.getBranchDetails(branch_slug);

        console.log("Branch data response:", response);
        if (response.status && response.data?.status_code === "000" && !response.data?.in_error) {
          const branch = response.data?.data?.[0];
          if (branch) {
            const isLocationCustom = !locationsByRegion[branch.region]?.includes(branch.location);
            
            setBranchData({
              name: branch.name,
              region: branch.region,
              location: isLocationCustom ? "Other" : branch.location,
              customLocation: isLocationCustom ? branch.location : "",
              district: branch.district || "",
              suburb: branch.suburb || "",
              landmark: branch.landmark || "",
              gpsAddress: branch.gpsAddress || "",
              googleMapsLink: branch.google_maps_link || "",
              phoneNumber: branch.phone_number ? branch.phone_number.replace("233", "") : "",
            });
            setIsCustomLocation(isLocationCustom);
          } else {
            throw new Error("Branch details not found");
          }
        } else {
          throw new Error(response.message || "Failed to fetch branch details");
        }
      } catch (error) {
        console.error("Error fetching branch:", error);
        toast.error(error.message || "Failed to fetch branch details");
        navigate("/branches");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranchDetails();
  }, [branch_slug, navigate, canEditBranches]);

  useEffect(() => {
    if (branchData.region) {
      const locations = locationsByRegion[branchData.region] || [];
      setAvailableLocations([...locations, "Other"]);
    }
  }, [branchData.region]);

  // NOW WE CAN HAVE CONDITIONAL RETURNS AFTER ALL HOOKS
  // Access denied screen for users without edit permissions
  if (!isAuthenticated || !canEditBranches) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-blue-50/30">
        <Navbar page="Edit Branch" />
        <div className="flex justify-center items-center min-h-[80vh]">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to edit branches. Please contact your administrator to request access to the Branch Editing feature.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Required Permission:</span>
              </div>
              <p className="text-sm text-blue-700">Edit Branch in Branch Management</p>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/branches')}
                className="px-4 py-2 font-medium text-white bg-gray-500 rounded-lg transition-colors hover:bg-gray-600"
              >
                View Branches
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 font-medium text-white bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] rounded-lg transition-colors hover:from-[#E55A59] hover:to-[#FF8584]"
              >
                Return to Dashboard
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return <Loader />;
  }

  const validateStep = (step) => {
    const newErrors = { ...errors };

    switch (step) {
      case 1:
        if (!branchData.name.trim()) {
          newErrors.name = "Branch name is required";
        } else if (branchData.name.length < 3) {
          newErrors.name = "Branch name must be at least 3 characters";
        } else if (branchData.name.length > 50) {
          newErrors.name = "Branch name must be less than 50 characters";
        } else {
          newErrors.name = "";
        }

        if (!branchData.phoneNumber) {
          newErrors.phoneNumber = "Phone number is required";
        } else if (branchData.phoneNumber.length !== 9) {
          newErrors.phoneNumber = "Phone number must be exactly 9 digits";
        } else if (!/^\d+$/.test(branchData.phoneNumber)) {
          newErrors.phoneNumber = "Phone number must contain only digits";
        } else {
          newErrors.phoneNumber = "";
        }
        break;

      case 2:
        if (!branchData.region) {
          newErrors.region = "Region is required";
        } else {
          newErrors.region = "";
        }

        if (!branchData.location && !isCustomLocation) {
          newErrors.location = "Location is required";
        } else {
          newErrors.location = "";
        }

        if (isCustomLocation && !branchData.customLocation.trim()) {
          newErrors.customLocation = "Custom location is required";
        } else {
          newErrors.customLocation = "";
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
    }
  };

  const handleRegionChange = (region) => {
    setBranchData((prev) => ({
      ...prev,
      region,
      location: "",
      customLocation: "",
    }));
    setIsCustomLocation(false);
    setErrors((prev) => ({ ...prev, region: "", location: "", customLocation: "" }));
  };

  const handleLocationChange = (location) => {
    setBranchData((prev) => ({
      ...prev,
      location,
      customLocation: "",
    }));
    setIsCustomLocation(location === "Other");
    setErrors((prev) => ({ ...prev, location: "", customLocation: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(1) || !validateStep(2)) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        name: branchData.name,
        region: branchData.region,
        location: isCustomLocation ? branchData.customLocation : branchData.location,
        district: branchData.district || "",
        suburb: branchData.suburb || "",
        landmark: branchData.landmark || "",
        gpsAddress: branchData.gpsAddress || "",
        googleMapsLink: branchData.googleMapsLink || "",
        phone_number: `233${branchData.phoneNumber}`
      };

      const response = await branchService.updateBranch(branch_slug, payload);

      if (response.status && response.data?.status_code === "000" && !response.data?.in_error) {
        toast.success(response.data?.reason || "Branch updated successfully");
        navigate("/branches");
      } else {
        throw new Error(response.message || "Failed to update branch");
      }
    } catch (error) {
      console.error("Error updating branch:", error);
      toast.error(error.message || "Failed to update branch");
    } finally {
      setIsSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="mb-8 text-center">
              <div className="inline-block p-4 mb-4 bg-gradient-to-r from-[#FF6F6E]/20 to-[#FFB5B4]/20 rounded-2xl">
                <Building2 className="w-8 h-8 text-[#FF6F6E]" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Basic Information</h2>
              <p className="text-gray-600">Update the essential details about your branch</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  Branch Name
                </label>
                <input
                  type="text"
                  value={branchData.name}
                  onChange={(e) => {
                    setBranchData((prev) => ({ ...prev, name: e.target.value }));
                    setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  className={`w-full px-4 py-3 text-lg border-2 ${
                    errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  } rounded-xl focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300`}
                  placeholder="Enter branch name"
                />
                <AnimatePresence>
                  {errors.name && <ValidationError message={errors.name} />}
                </AnimatePresence>
              </div>

              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  Phone Number
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="flex absolute inset-y-0 left-0 items-center pl-4 pointer-events-none">
                    <span className="text-gray-500">233</span>
                  </div>
                  <input
                    type="text"
                    value={branchData.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 9) {
                        setBranchData((prev) => ({
                          ...prev,
                          phoneNumber: value,
                        }));
                        setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                      }
                    }}
                    className={`w-full pl-14 pr-4 py-3 text-lg border-2 ${
                      errors.phoneNumber ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    } rounded-xl focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300`}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter 9 digits without the prefix
                </p>
                <AnimatePresence>
                  {errors.phoneNumber && <ValidationError message={errors.phoneNumber} />}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="mb-8 text-center">
              <div className="inline-block p-4 mb-4 bg-gradient-to-r from-green-100 to-green-200 rounded-2xl">
                <MapPin className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Location Details</h2>
              <p className="text-gray-600">Update where your branch is located</p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  Region
                </label>
                <div className="relative">
                  <select
                    value={branchData.region}
                    onChange={(e) => handleRegionChange(e.target.value)}
                    className={`w-full px-4 py-3 text-lg border-2 ${
                      errors.region ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    } rounded-xl focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 appearance-none bg-white hover:border-gray-300 cursor-pointer`}
                  >
                    <option value="">Select Region</option>
                    {regionOptions.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 p-1.5 bg-blue-100 rounded-lg -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                <AnimatePresence>
                  {errors.region && <ValidationError message={errors.region} />}
                </AnimatePresence>
              </div>

              {branchData.region && (
                <div>
                  <label className="block mb-2 text-lg font-semibold text-gray-800">
                    Location
                  </label>
                  <div className="relative">
                    <select
                      value={branchData.location}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      className={`w-full px-4 py-3 text-lg border-2 ${
                        errors.location ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      } rounded-xl focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 appearance-none bg-white hover:border-gray-300 cursor-pointer`}
                    >
                      <option value="">Select Location</option>
                      {availableLocations.map((location) => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 p-1.5 bg-blue-100 rounded-lg -translate-y-1/2 pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-blue-600" />
                    </div>
                  </div>
                  <AnimatePresence>
                    {errors.location && <ValidationError message={errors.location} />}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {isCustomLocation && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  Custom Location
                </label>
                <input
                  type="text"
                  value={branchData.customLocation}
                  onChange={(e) =>
                    setBranchData((prev) => ({
                      ...prev,
                      customLocation: e.target.value,
                    }))
                  }
                  className={`w-full px-4 py-3 text-lg border-2 ${
                    errors.customLocation ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  } rounded-xl focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300`}
                  placeholder="Enter custom location"
                />
                <AnimatePresence>
                  {errors.customLocation && <ValidationError message={errors.customLocation} />}
                </AnimatePresence>
              </motion.div>
            )}
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="mb-8 text-center">
              <div className="inline-block p-4 mb-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded-2xl">
                <Info className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Additional Details</h2>
              <p className="text-gray-600">Update additional information to help customers find your branch</p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  District <OptionalLabel />
                </label>
                <input
                  type="text"
                  value={branchData.district}
                  onChange={(e) =>
                    setBranchData((prev) => ({
                      ...prev,
                      district: e.target.value,
                    }))
                  }
                  className="px-4 py-3 w-full text-lg rounded-xl border-2 border-gray-200 transition-all duration-200 focus:ring-[#FF6F6E] focus:border-[#FF6F6E] placeholder:text-gray-400 hover:border-gray-300"
                  placeholder="Enter district name"
                />
              </div>

              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  Suburb <OptionalLabel />
                </label>
                <input
                  type="text"
                  value={branchData.suburb}
                  onChange={(e) =>
                    setBranchData((prev) => ({
                      ...prev,
                      suburb: e.target.value,
                    }))
                  }
                  className="px-4 py-3 w-full text-lg rounded-xl border-2 border-gray-200 transition-all duration-200 focus:ring-[#FF6F6E] focus:border-[#FF6F6E] placeholder:text-gray-400 hover:border-gray-300"
                  placeholder="Enter suburb name"
                />
              </div>

              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  Landmark <OptionalLabel />
                </label>
                <input
                  type="text"
                  value={branchData.landmark}
                  onChange={(e) =>
                    setBranchData((prev) => ({
                      ...prev,
                      landmark: e.target.value,
                    }))
                  }
                  className="px-4 py-3 w-full text-lg rounded-xl border-2 border-gray-200 transition-all duration-200 focus:ring-[#FF6F6E] focus:border-[#FF6F6E] placeholder:text-gray-400 hover:border-gray-300"
                  placeholder="Enter a nearby landmark"
                />
              </div>

              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  GPS Address <OptionalLabel />
                </label>
                <input
                  type="text"
                  value={branchData.gpsAddress}
                  onChange={(e) =>
                    setBranchData((prev) => ({
                      ...prev,
                      gpsAddress: e.target.value,
                    }))
                  }
                  className="px-4 py-3 w-full text-lg rounded-xl border-2 border-gray-200 transition-all duration-200 focus:ring-[#FF6F6E] focus:border-[#FF6F6E] placeholder:text-gray-400 hover:border-gray-300"
                  placeholder="Enter GPS address"
                />
              </div>

              <div>
                <label className="block mb-2 text-lg font-semibold text-gray-800">
                  Google Maps Link <OptionalLabel />
                </label>
                <input
                  type="url"
                  value={branchData.googleMapsLink}
                  onChange={(e) =>
                    setBranchData((prev) => ({
                      ...prev,
                      googleMapsLink: e.target.value,
                    }))
                  }
                  className="px-4 py-3 w-full text-lg rounded-xl border-2 border-gray-200 transition-all duration-200 focus:ring-[#FF6F6E] focus:border-[#FF6F6E] placeholder:text-gray-400 hover:border-gray-300"
                  placeholder="https://maps.google.com/..."
                />
              </div>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="mb-8 text-center">
              <div className="inline-block p-4 mb-4 bg-gradient-to-r from-green-100 to-green-200 rounded-2xl">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Review & Confirm</h2>
              <p className="text-gray-600">Please review your branch information before updating</p>
            </div>

            <div className="p-6 space-y-4 bg-gray-50 rounded-2xl">
              <div className="flex gap-3 items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Branch Name</p>
                  <p className="font-semibold text-gray-900">{branchData.name}</p>
                </div>
              </div>

              <div className="flex gap-3 items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Phone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone Number</p>
                  <p className="font-semibold text-gray-900">233{branchData.phoneNumber}</p>
                </div>
              </div>

              <div className="flex gap-3 items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="font-semibold text-gray-900">
                    {branchData.region} - {isCustomLocation ? branchData.customLocation : branchData.location}
                  </p>
                </div>
              </div>

              {(branchData.district || branchData.suburb || branchData.landmark) && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="mb-3 text-sm font-medium text-gray-700">Additional Details</h4>
                  <div className="space-y-2">
                    {branchData.district && (
                      <div className="flex gap-2 items-center text-sm text-gray-600">
                        <Navigation className="w-4 h-4 text-gray-400" />
                        <span>District: {branchData.district}</span>
                      </div>
                    )}
                    {branchData.suburb && (
                      <div className="flex gap-2 items-center text-sm text-gray-600">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <span>Suburb: {branchData.suburb}</span>
                      </div>
                    )}
                    {branchData.landmark && (
                      <div className="flex gap-2 items-center text-sm text-gray-600">
                        <Star className="w-4 h-4 text-gray-400" />
                        <span>Landmark: {branchData.landmark}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Navbar page="Edit Branch" />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="p-6 py-8 mx-auto max-w-6xl"
        >
          {/* Back Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/branches")}
            className="flex gap-2 items-center mb-6 text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Branches</span>
          </motion.button>

          {/* Step Indicator */}
          <StepIndicator 
            currentStep={currentStep} 
            totalSteps={4} 
            onStepClick={handleStepClick}
          />

          {/* Form Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="p-8 bg-white rounded-3xl border border-gray-100 shadow-xl"
          >
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6 mt-8 border-t border-gray-200">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className="flex gap-2 items-center px-6 py-3 text-gray-600 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span>Previous</span>
              </motion.button>

              {currentStep < 4 ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNext}
                  className="flex gap-2 items-center px-6 py-3 text-white bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] rounded-xl shadow-lg transition-all duration-200 hover:from-[#E55A59] hover:to-[#FF8584]"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="flex gap-2 items-center px-6 py-3 text-white bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] rounded-xl shadow-lg transition-all duration-200 hover:from-[#E55A59] hover:to-[#FF8584] disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving Changes...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default BranchEdit;
